import React, { useState, useCallback, useEffect, useRef } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';

const BACKEND_URL = getBackendUrl();

// Helper to get token
const getToken = () => storage.getItem('token');

/**
 * Song Year Manager
 * -----------------
 * Single-purpose admin panel for the release_year database.
 *
 * Design principles (May 2026 simplification):
 *   • One safe upload action that ALWAYS smart-merges (no replace, no
 *     destructive prompts). Adds new songs, fills missing years, never
 *     deletes anything.
 *   • One auto-enrichment action via MusicBrainz for songs without year.
 *   • Stats + manual search/edit for fine-grained corrections.
 *
 * Removed (data-loss risk + UI clutter):
 *   - "Replace mode" with typed-confirmation prompts
 *   - Separate "Direct Seed Upload" panel (merged into the single upload)
 *   - "Διαγνωστικά" button (the "JSON missing on production" message it
 *     surfaced was misleading — it's normal because the static JSON is
 *     ephemeral; data is safely persisted in MongoDB)
 *   - "Επανεκκίνηση από JSON" recovery button (no longer needed now that
 *     MongoDB is the canonical source of truth)
 */
const SongYearManager = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [selectedSong, setSelectedSong] = useState(null);
  const [newYear, setNewYear] = useState('');
  const [updating, setUpdating] = useState(false);
  const [message, setMessage] = useState(null);
  const [recentUpdates, setRecentUpdates] = useState([]);

  // Stats + uploads
  const [dbStats, setDbStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  // MusicBrainz auto-enrichment
  const [isEnriching, setIsEnriching] = useState(false);
  const uploadRef = useRef(null);

  const fetchDbStats = useCallback(async () => {
    setStatsLoading(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/playlist-analyzer/admin/db/stats`);
      if (r.ok) setDbStats(await r.json());
    } catch (e) {
      // silent fail — stats are informational only
    } finally {
      setStatsLoading(false);
    }
  }, []);

  useEffect(() => { fetchDbStats(); }, [fetchDbStats]);

  // ── ONE safe upload flow ──
  // Always smart-merge: adds new songs, fills missing years, never deletes.
  // Accepts CSV / JSON / XLSX. Routed to /seed-from-upload?force=false
  // which works in both production (MongoDB-direct) and preview environments.
  const handleSafeUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadResult(null);
    setMessage(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = getToken();
      const r = await fetch(
        `${BACKEND_URL}/api/playlist-analyzer/admin/db/seed-from-upload?force=false`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${token}` },
          body: formData,
        }
      );
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      const data = await r.json();
      setUploadResult(data);
      // Both endpoints expose songs_inserted/songs_year_updated/songs_skipped
      const inserted = data.songs_inserted ?? data.songs_added ?? 0;
      const yearUpdated = data.songs_year_updated ?? data.songs_updated ?? 0;
      const skipped = data.songs_skipped ?? 0;
      const total = data.total_in_mongo ?? data.total_songs_in_db ?? '—';
      const parts = [];
      if (inserted > 0) parts.push(`+${inserted} νέα`);
      if (yearUpdated > 0) parts.push(`↻ ${yearUpdated} έτη ενημερώθηκαν`);
      if (skipped > 0) parts.push(`⏭ ${skipped} ήδη υπήρχαν`);
      if (parts.length === 0) parts.push('καμία αλλαγή');
      setMessage({
        type: 'success',
        text: `✅ Επιτυχία: ${parts.join(' · ')} · σύνολο ${total} τραγούδια στη βάση`,
      });
      fetchDbStats();
    } catch (err) {
      setMessage({ type: 'error', text: `❌ Σφάλμα upload: ${err.message}` });
    } finally {
      setUploading(false);
      if (uploadRef.current) uploadRef.current.value = '';
    }
  };

  // ── MusicBrainz auto-enrichment for songs without year ──
  const handleAutoReleaseYears = useCallback(async () => {
    setIsEnriching(true);
    setMessage({ type: 'info', text: '🔍 Αναζήτηση release years από MusicBrainz...' });
    try {
      const token = getToken();
      const r = await fetch(
        `${BACKEND_URL}/api/playlist-analyzer/admin/db/enrich-release-years?category=all&limit=50&dry_run=false`,
        {
          method: 'POST',
          headers: token ? { 'Authorization': `Bearer ${token}` } : {},
        }
      );
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      if (data.updated > 0) {
        setMessage({
          type: 'success',
          text: `✅ MusicBrainz: ${data.updated}/${data.processed} release years βρέθηκαν · υπολειπόμενα χωρίς έτος: ${data.remaining_without_year ?? '—'}`,
        });
        fetchDbStats();
      } else {
        setMessage({
          type: 'info',
          text: 'ℹ️ Δεν βρέθηκαν νέα release years σε αυτή τη παρτίδα.',
        });
      }
    } catch (err) {
      setMessage({ type: 'error', text: `❌ Σφάλμα MusicBrainz: ${err.message}` });
    } finally {
      setIsEnriching(false);
    }
  }, [fetchDbStats]);

  // Search for songs (uses XHR to bypass rrweb fetch interceptor that
  // breaks Response cloning, causing "Response body is already used")
  const searchSongs = useCallback(() => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    setMessage(null);
    const token = getToken();
    const xhr = new XMLHttpRequest();
    xhr.open('GET', `${BACKEND_URL}/api/admin/config/song-years/search?query=${encodeURIComponent(searchQuery)}`);
    xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    xhr.onload = () => {
      setSearching(false);
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const data = JSON.parse(xhr.responseText);
          setSearchResults(data.songs || []);
          if ((data.songs || []).length === 0) {
            setMessage({ type: 'info', text: 'Δεν βρέθηκαν τραγούδια με αυτά τα κριτήρια' });
          }
        } catch (parseErr) {
          setMessage({ type: 'error', text: `Σφάλμα ανάλυσης: ${parseErr.message}` });
        }
      } else {
        setMessage({ type: 'error', text: `Σφάλμα αναζήτησης: HTTP ${xhr.status}` });
      }
    };
    xhr.onerror = () => {
      setSearching(false);
      setMessage({ type: 'error', text: 'Σφάλμα δικτύου κατά την αναζήτηση' });
    };
    xhr.send();
  }, [searchQuery]);

  // Update song year
  const updateSongYear = useCallback(async () => {
    if (!selectedSong || !newYear) return;
    const yearNum = parseInt(newYear, 10);
    if (isNaN(yearNum) || yearNum < 1900 || yearNum > new Date().getFullYear()) {
      setMessage({ type: 'error', text: 'Παρακαλώ εισάγετε έγκυρο έτος (1900 - σήμερα)' });
      return;
    }
    setUpdating(true);
    setMessage(null);
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/config/song-years/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          artist: selectedSong.artist,
          title: selectedSong.title,
          release_year: yearNum
        })
      });
      if (!response.ok) throw new Error('Update failed');
      const data = await response.json();
      setSearchResults(prev => prev.map(s =>
        s.artist === selectedSong.artist && s.title === selectedSong.title
          ? { ...s, release_year: yearNum }
          : s
      ));
      setRecentUpdates(prev => [{
        artist: selectedSong.artist,
        title: selectedSong.title,
        year: yearNum,
        timestamp: new Date()
      }, ...prev].slice(0, 10));
      setMessage({
        type: 'success',
        text: `✅ Ενημερώθηκαν ${data.updated_count || 0} εγγραφές για "${selectedSong.artist} - ${selectedSong.title}"`
      });
      setSelectedSong(null);
      setNewYear('');
    } catch (err) {
      setMessage({ type: 'error', text: `Σφάλμα ενημέρωσης: ${err.message}` });
    } finally {
      setUpdating(false);
    }
  }, [selectedSong, newYear]);

  // Handle keyboard shortcuts
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (selectedSong && newYear) {
        updateSongYear();
      } else {
        searchSongs();
      }
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
          📅 Διαχείριση Ετών Κυκλοφορίας
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Αναζήτηση και διόρθωση ετών κυκλοφορίας τραγουδιών
        </p>
      </div>

      {/* ── Database Stats Panel + top actions ── */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4" data-testid="year-db-stats">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
            📊 Στατιστικά Βάσης Ετών
          </h3>
          <div className="flex gap-2 flex-wrap">
            <button
              onClick={fetchDbStats}
              disabled={statsLoading}
              className="text-xs px-3 py-1.5 rounded-md bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200"
              data-testid="refresh-year-stats-btn"
            >
              {statsLoading ? '⏳' : '🔄'} Ανανέωση
            </button>
            <button
              onClick={() => {
                // Trigger a download via the export endpoint. We use a hidden
                // <a> click so the browser handles the file save dialog.
                const url = `${BACKEND_URL}/api/playlist-analyzer/admin/db/export`;
                const a = document.createElement('a');
                a.href = url;
                a.download = '';
                a.target = '_self';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
              }}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 text-white font-medium"
              data-testid="download-db-backup-btn"
              title="Κατέβασμα τρέχουσας Mongo βάσης ως JSON αρχείο (backup)"
            >
              📥 Λήψη Backup
            </button>
            <button
              onClick={handleAutoReleaseYears}
              disabled={isEnriching || uploading}
              className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 hover:bg-emerald-600 disabled:bg-emerald-300 dark:disabled:bg-emerald-900/40 text-white font-medium"
              data-testid="auto-release-years-btn"
              title="Αυτόματη αναζήτηση release years από MusicBrainz (50 τραγούδια/φορά). Δεν αντικαθιστά υπάρχοντα έτη."
            >
              {isEnriching ? '⏳ Αναζήτηση...' : '📅 Auto Release Years'}
            </button>
          </div>
        </div>
        {dbStats ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <StatCard label="Σύνολο τραγουδιών" value={dbStats.total_songs} accent="indigo" testId="stat-total-songs" />
            <StatCard label="Με έτος" value={dbStats.songs_with_year} accent="green" testId="stat-songs-with-year" />
            <StatCard label="Χωρίς έτος" value={dbStats.songs_without_year || 0} accent="amber" testId="stat-songs-without-year" />
            <StatCard label="Καλλιτέχνες" value={dbStats.unique_artists} accent="blue" testId="stat-unique-artists" />
            <StatCard label="Καλλ. με έτος" value={dbStats.unique_artists_with_year || 0} accent="purple" testId="stat-artists-with-year" />
            <StatCard
              label="Κάλυψη"
              value={dbStats.total_songs > 0 ? `${Math.round((dbStats.songs_with_year / dbStats.total_songs) * 100)}%` : '—'}
              accent="teal"
              testId="stat-year-coverage"
            />
          </div>
        ) : (
          <div className="text-sm text-gray-500 dark:text-gray-400 italic">Φόρτωση...</div>
        )}
        {dbStats?.by_category_with_year && (
          <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700 flex flex-wrap gap-x-6 gap-y-1 text-xs text-gray-600 dark:text-gray-400">
            <span>Ελληνικά με έτος: <strong className="text-gray-800 dark:text-gray-200">{dbStats.by_category_with_year.greek}</strong> / {dbStats.greek_songs}</span>
            <span>Διεθνή με έτος: <strong className="text-gray-800 dark:text-gray-200">{dbStats.by_category_with_year.global}</strong> / {dbStats.global_songs}</span>
            <span>Classics με έτος: <strong className="text-gray-800 dark:text-gray-200">{dbStats.by_category_with_year.classics}</strong> / {dbStats.classics_songs}</span>
          </div>
        )}
      </div>

      {/* ── Single Safe Upload Panel ──
          Smart-merge only:
            ➕ νέα τραγούδια προστίθενται
            ↻ υπάρχοντα με διαφορετικό έτος ενημερώνονται
            ⏭ υπάρχοντα ίδια παρακάμπτονται
          Αδύνατο να διαγραφούν δεδομένα. */}
      <div
        className="bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 rounded-lg shadow p-4 border border-emerald-200 dark:border-emerald-800"
        data-testid="safe-upload-panel"
      >
        <h3 className="text-lg font-semibold text-emerald-800 dark:text-emerald-200 mb-1">
          📤 Ανέβασμα Αρχείου Ετών
        </h3>
        <p className="text-sm text-emerald-700 dark:text-emerald-300 mb-3">
          Δέχεται <strong>CSV</strong>, <strong>JSON</strong> ή <strong>XLSX</strong>.
          Αναμενόμενες στήλες: <code>artist</code>, <code>title</code>, <code>year</code> (ή <code>release</code>).
          <br />
          <span className="text-xs">
            🛡️ <strong>Ασφαλής λειτουργία:</strong> προσθέτει νέα τραγούδια, συμπληρώνει έτη που λείπουν, ενημερώνει
            λάθος έτη — <strong>ποτέ δεν διαγράφει δεδομένα</strong>.
          </span>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <input
            ref={uploadRef}
            type="file"
            accept=".json,.csv,.xlsx"
            onChange={handleSafeUpload}
            disabled={uploading}
            className="hidden"
            data-testid="safe-upload-input"
          />
          <button
            onClick={() => uploadRef.current?.click()}
            disabled={uploading}
            className={`px-5 py-2 rounded-lg font-medium text-white transition-colors ${
              uploading ? 'bg-emerald-400 cursor-wait' : 'bg-emerald-600 hover:bg-emerald-700'
            }`}
            data-testid="safe-upload-btn"
          >
            {uploading ? '⏳ Επεξεργασία...' : '📤 Επιλογή αρχείου'}
          </button>
          {uploadResult && (
            <div className="text-xs text-gray-700 dark:text-gray-300 space-y-0.5">
              {(uploadResult.songs_inserted ?? uploadResult.songs_added) > 0 && (
                <div>📥 +{uploadResult.songs_inserted ?? uploadResult.songs_added} νέα τραγούδια</div>
              )}
              {(uploadResult.songs_year_updated ?? uploadResult.songs_updated) > 0 && (
                <div>↻ {uploadResult.songs_year_updated ?? uploadResult.songs_updated} έτη ενημερώθηκαν</div>
              )}
              {uploadResult.songs_skipped > 0 && (
                <div>⏭ {uploadResult.songs_skipped} skipped (ήδη υπήρχαν με ίδιο έτος)</div>
              )}
              {uploadResult.enrichment_reload?.reloaded && (
                <div>🔄 Live reload: {uploadResult.enrichment_reload.lookup_entries} entries</div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">🔍 Αναζήτηση Τραγουδιού</h3>
        <div className="flex gap-3">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Καλλιτέχνης ή τίτλος τραγουδιού..."
            className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            data-testid="song-search-input"
          />
          <button
            onClick={searchSongs}
            disabled={searching || !searchQuery.trim()}
            className={`px-6 py-2 rounded-lg font-medium ${
              searching
                ? 'bg-purple-400 cursor-wait'
                : 'bg-purple-600 hover:bg-purple-700'
            } text-white transition-colors`}
            data-testid="song-search-btn"
          >
            {searching ? '⏳ Αναζήτηση...' : '🔍 Αναζήτηση'}
          </button>
        </div>
      </div>

      {/* Message */}
      {message && (
        <div className={`p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300' :
          message.type === 'error' ? 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300' :
          'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Search Results */}
      {searchResults.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              📋 Αποτελέσματα ({searchResults.length})
            </h3>
          </div>
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="bg-gray-100 dark:bg-gray-700 sticky top-0">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Καλλιτέχνης</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Τίτλος</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Έτος</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Plays</th>
                  <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Ενέργεια</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {searchResults.map((song, idx) => (
                  <tr
                    key={idx}
                    className={`hover:bg-gray-50 dark:hover:bg-gray-700/50 ${
                      selectedSong?.artist === song.artist && selectedSong?.title === song.title
                        ? 'bg-purple-50 dark:bg-purple-900/20'
                        : ''
                    }`}
                  >
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">{song.artist}</td>
                    <td className="px-4 py-3 text-sm text-gray-800 dark:text-white">{song.title}</td>
                    <td className="px-4 py-3 text-sm text-center">
                      {song.release_year ? (
                        <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded">
                          {song.release_year}
                        </span>
                      ) : (
                        <span className="px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300 rounded">
                          Άγνωστο
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-center text-gray-600 dark:text-gray-400">
                      {song.play_count?.toLocaleString() || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => {
                          setSelectedSong(song);
                          setNewYear(song.release_year?.toString() || '');
                        }}
                        className="px-3 py-1 bg-purple-600 text-white text-xs rounded hover:bg-purple-700 transition-colors"
                        data-testid={`edit-song-${idx}`}
                      >
                        ✏️ Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Edit Form */}
      {selectedSong && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <h3 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">
            ✏️ Επεξεργασία: {selectedSong.artist} - {selectedSong.title}
          </h3>
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Έτος Κυκλοφορίας
              </label>
              <input
                type="number"
                value={newYear}
                onChange={(e) => setNewYear(e.target.value)}
                onKeyPress={handleKeyPress}
                min="1900"
                max={new Date().getFullYear()}
                placeholder="π.χ. 2023"
                className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-800 dark:text-white focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                data-testid="year-input"
              />
            </div>
            <div className="flex gap-2 pt-6">
              <button
                onClick={updateSongYear}
                disabled={updating || !newYear}
                className={`px-4 py-2 rounded-lg font-medium ${
                  updating
                    ? 'bg-green-400 cursor-wait'
                    : 'bg-green-600 hover:bg-green-700'
                } text-white transition-colors`}
                data-testid="save-year-btn"
              >
                {updating ? '⏳ Αποθήκευση...' : '💾 Αποθήκευση'}
              </button>
              <button
                onClick={() => { setSelectedSong(null); setNewYear(''); }}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                ❌ Ακύρωση
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recent Updates */}
      {recentUpdates.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
            <h3 className="font-semibold text-gray-800 dark:text-white">
              🕐 Πρόσφατες Ενημερώσεις
            </h3>
          </div>
          <div className="divide-y divide-gray-200 dark:divide-gray-700">
            {recentUpdates.map((update, idx) => (
              <div key={idx} className="px-4 py-2 flex items-center justify-between">
                <span className="text-sm text-gray-800 dark:text-white">
                  {update.artist} - {update.title}
                </span>
                <div className="flex items-center gap-3">
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-sm rounded">
                    {update.year}
                  </span>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {update.timestamp.toLocaleTimeString('el-GR')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help */}
      <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
        <h4 className="font-medium text-blue-800 dark:text-blue-200 mb-2">💡 Συμβουλές</h4>
        <ul className="text-sm text-blue-700 dark:text-blue-300 space-y-1">
          <li>• Αναζητήστε με τον καλλιτέχνη ή τον τίτλο του τραγουδιού</li>
          <li>• Οι αλλαγές εφαρμόζονται σε όλες τις εγγραφές του τραγουδιού</li>
          <li>• Τα τραγούδια με &quot;Άγνωστο&quot; έτος θα εμπλουτιστούν αυτόματα από τα scheduled jobs</li>
          <li>• Πατήστε Enter για γρήγορη αποθήκευση</li>
        </ul>
      </div>
    </div>
  );
};

export default SongYearManager;

// ── Helper: small stat tile ──
const ACCENT_BG = {
  indigo: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-300',
  green: 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300',
  amber: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300',
  blue: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',
  purple: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300',
  teal: 'bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300',
};

function StatCard({ label, value, accent = 'indigo', testId }) {
  return (
    <div
      className={`rounded-lg p-3 ${ACCENT_BG[accent] || ACCENT_BG.indigo}`}
      data-testid={testId}
    >
      <div className="text-[10px] uppercase tracking-wide opacity-70 font-medium">{label}</div>
      <div className="text-2xl font-bold tabular-nums mt-0.5">{value?.toLocaleString?.('el-GR') ?? value}</div>
    </div>
  );
}
